<template>
  <v-dialog
    :value="rentUnitModal"
    fullscreen
    hide-overlay
    persistent
    transition="dialog-bottom-transition"
    :content-class="twilioChat ? 'twilioDrawer' : ''"
  >
    <v-card v-if="rentUnitModal" flat>
      <v-card-title class="text-h5" primary-title flat>
        <span v-if="recording">Rent Unit</span>
        <span v-else class="error--text">
          Please complete credit card retrieval. You must complete retrieval before performing other actions.
        </span>
      </v-card-title>
      <v-divider />
      <v-card-text>
        <v-stepper v-model="currentStep" flat>
          <v-stepper-header>
            <v-stepper-step
              v-show="updateNeeded"
              :color="currentStep === 0 ? 'secondary' : 'success'"
              :complete="currentStep > 0"
              step="0"
              :class="currentStep === 0 ? 'title' : 'subheading'"
            >
              Select Unit
            </v-stepper-step>
            <v-divider v-show="updateNeeded" :class="currentStep > 0 ? 'success' : null" />
            <v-stepper-step
              :color="currentStep === 1 ? 'secondary' : 'success'"
              :complete="currentStep > 1"
              step="1"
              :class="currentStep === 1 ? 'title' : 'subheading'"
            >
              Rental Options
            </v-stepper-step>
            <v-divider :class="currentStep > 1 ? 'success' : null" />
            <v-stepper-step
              :color="currentStep <= 2 ? 'secondary' : 'success'"
              :complete="currentStep > 2"
              step="2"
              :class="currentStep === 2 ? 'title' : 'subheading'"
            >
              Lease Info
            </v-stepper-step>
            <v-divider :class="currentStep > 2 ? 'success' : null" />
            <v-stepper-step
              :color="currentStep <= 3 ? 'secondary' : 'success'"
              :complete="currentStep > 3"
              step="3"
              :class="currentStep === 3 ? 'title' : 'subheading'"
            >
              Move-In Instructions
            </v-stepper-step>
            <v-divider :class="currentStep > 3 ? 'success' : null" />
            <v-stepper-step
              :color="currentStep <= 4 ? 'secondary' : 'success'"
              :complete="currentStep > 4"
              step="4"
              :class="currentStep === 4 ? 'title' : 'subheading'"
            >
              Add Merchandise
            </v-stepper-step>
            <v-divider :class="currentStep > 4 ? 'success' : null" />
            <v-stepper-step
              :color="currentStep <= 5 ? 'secondary' : 'success'"
              :complete="currentStep > 5"
              step="5"
              :class="currentStep === 5 ? 'title' : 'subheading'"
            >
              Payment Options
            </v-stepper-step>
            <v-divider :class="currentStep > 5 ? 'success' : null" />
            <v-stepper-step
              :color="currentStep < 6 ? 'secondary' : 'success'"
              :complete="currentStep > 6"
              step="6"
              :class="currentStep === 6 ? 'title' : 'subheading'"
            >
              Rental Review
            </v-stepper-step>
            <v-divider :class="currentStep > 6 ? 'success' : null" />
            <v-stepper-step
              :color="currentStep === 7 ? 'success' : null"
              :complete="currentStep === 7"
              step="7"
              :class="currentStep === 7 ? 'title' : 'subheading'"
            >
              Rental Success
            </v-stepper-step>
          </v-stepper-header>
          <v-stepper-items :class="darkMode ? 'grey darken-3' : 'grey lighten-4'">
            <v-layout>
              <v-flex :xs8="currentStep > 0 && currentStep < 4">
                <v-stepper-content step="0" v-show="updateNeeded">
                  <unit-select
                    :reservation="reservation"
                    :availableUnits="availableUnits"
                    :infoLoading="infoLoading"
                    @setUnit="setUnit($event)"
                  />
                </v-stepper-content>
                <v-stepper-content step="1">
                  <rental-options
                    :panel="panel"
                    :promotionOffers="promotionOffers"
                    :updateNeeded="updateNeeded"
                    :tpp="tpp"
                    @setUnit="setUnit($event)"
                  />
                </v-stepper-content>
                <v-stepper-content step="2">
                  <lease-info
                    :panel="panel"
                    :promotionOffers="promotionOffers"
                    :promotionMonth="promotionMonth"
                    :updateNeeded="updateNeeded"
                  />
                </v-stepper-content>
                <v-stepper-content step="3">
                  <move-in-instructions />
                </v-stepper-content>
                <v-stepper-content step="4">
                  <add-merchandise
                    @getMoveInCost="getMoveInCost"
                  />
                </v-stepper-content>
                <v-stepper-content step="5">
                  <payment-options :updateNeeded="updateNeeded" />
                </v-stepper-content>
                <v-stepper-content step="6">
                  <rental-review
                    v-if="currentStep === 6"
                    :currentStep="currentStep"
                    :promotionOffers="promotionOffers"
                    :promotionMonth="promotionMonth"
                    :updateNeeded="updateNeeded"
                    :payload="moveInPayload"
                  />
                </v-stepper-content>
                <v-stepper-content step="7">
                  <rental-complete
                    v-if="currentStep === 7"
                    :promotionMonth="promotionMonth"
                    :payload="moveInPayload"
                  />
                </v-stepper-content>
              </v-flex>
              <v-flex xs4 v-if="currentStep > 0 && currentStep < 6">
                <side-total
                  class="pa-4"
                  :promotionOffers="promotionOffers"
                  :promotionMonth="promotionMonth"
                />
                </v-flex>
            </v-layout>
            <button-navigation
              class="mx-3 mb-3"
              v-show="currentStep < 7"
              :currentStep="currentStep"
              :updateNeeded="updateNeeded"
            />
          </v-stepper-items>
        </v-stepper>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <stepper-navigation
          :currentStep="currentStep"
          :loading="loading"
          :newUnitSelected="newUnitSelected"
          :paymentLoading="paymentLoading"
          @success="collapseAfterSuccess"
          @collapse="collapse"
          @unitUpdated="unitUpdated"
          @toLease="toLeaseInfo"
          @toMoveIn="toMoveInInstructions"
          @speederent="speederent"
          @toMerch="toAddMerchandise"
          @toPayment="toTakePayment"
          @toSummary="toSummary"
          @moveIn="submitMoveIn"
        />
      </v-card-actions>
    </v-card>
    <application-modal
      v-model="merchFailModal"
      :hideCancel="true"
    >
      <div slot="modal-title">
        <v-layout align-center>
          <v-icon class="mr-2">error</v-icon>
          Merchandise Sale Unsuccessful
        </v-layout>
      </div>
      <div slot="modal-body" class="subheading">
        <div class="mb-3">We were unable to process the customer's merchandise sale:
          <span class="error--text">{{ merchFailMessage }}</span>
        </div>
        <div>
          Please inform the customer and advise him/her to purchase the products on-site on the
          move-in date.
        </div>
      </div>
      <v-btn
        data-testid="rent-dismiss-merch-modal-btn"
        class="rent-dismiss-merch-modal-btn primary"
        slot="create-button"
        @click="merchFailModal = false"
      >
        Dismiss
      </v-btn>
    </application-modal>
    <application-modal v-model="speederentModal">
      <div slot="modal-title">
        <v-layout align-center>
          <!-- <v-icon class="mr-2">error</v-icon> -->
          Speederent Confirmation
        </v-layout>
      </div>
      <div slot="modal-body">
        <div class="mb-3">Allow customer to complete the move in via Speederent?
        </div>
        <div>
          Send email to: {{ info.email }}
        </div>
        <div>
          Send SMS to: {{ info.phone_number }}
          </div>
      </div>
      <v-btn
        class="primary"
        slot="create-button"
        @click="doSpeederent"
      >
        Speederent
      </v-btn>
    </application-modal>
  </v-dialog>
</template>

<script>
/* eslint-disable no-return-assign */
import { mapGetters, mapState } from "vuex";
import AddMerchandise from "@/components/rent-unit/steps/AddMerchandise";
import ApplicationModal from "@/components/ApplicationModal";
import ButtonNavigation from "@/components/rent-unit/partials/ButtonNavigation";
import { EventBus } from "@/Event";
import LeaseInfo from "@/components/rent-unit/steps/LeaseInfo";
import MoveInInstructions from "@/components/rent-unit/steps/MoveInInstructions";
import PaymentOptions from "@/components/rent-unit/steps/PaymentOptions";
import RentalComplete from "@/components/rent-unit/steps/RentalComplete";
import RentalOptions from "@/components/rent-unit/steps/RentalOptions";
import RentalReview from "@/components/rent-unit/steps/RentalReview";
import SideTotal from "@/components/rent-unit/partials/SideTotal";
import StepperNavigation from "@/components/rent-unit/partials/StepperNavigation";
import UnitSelect from "@/components/rent-unit/steps/UnitSelect";
import { dateMethodMixins } from "@/mixins/dateMethodMixins";
import moment from "moment";

export default {
  props: {
    availableUnits: {
      type: Array
    },
    infoLoading: {
      type: Boolean,
      default: false
    },
    rentUnitModal: {
      type: Boolean,
      default: false
    },
    rentUnitStep: {
      type: Number,
      default: 0
    },
    reservation: {
      type: [Object, String] // @TODO: TD - should be normalized to just being an obj at some point
    },
    tpp: {
      type: Array,
      default: () => []
    },
    updateNeeded: {
      type: Boolean,
      default: false
    }
  },

  mixins: [dateMethodMixins],

  data() {
    return {
      loading: false,
      paymentLoading: false,
      panel: 0,
      merchFailModal: false,
      merchFailMessage: "",
      speederentModal: false,
      gateCode: "",
      leaseNumber: "",
      newUnitSelected: false,
      updatedUnit: "",
      merchReceiptNumber: 0
    };
  },

  methods: {
    getMoveInCost() {
      const payload = {
        facilityId: this.$route.params.facilityId
      };
      this.$store.dispatch("getMoveInCost", payload).catch(err => {
        this.$store.dispatch("handleError", err);
      });
    },
    setUnit(updatedUnit) {
      this.$store.commit("moveInCostLoading", true);
      updatedUnit.can_update = true;
      this.newUnitSelected = true;
      this.updatedUnit = updatedUnit;
      this.$emit("setUnit", updatedUnit);
      let payload = {
        facilityId: this.$route.params.facilityId,
        waitingListId: this.info.waiting_id,
        move_in_date: moment(this.options.moveInDate).format("MM/DD/YYYY"),
        followup_date: moment(this.options.followupDate).format("MM/DD/YYYY"),
        tenant_id: this.info.externalId || null,
        status: 0,
        is_follow_up: false,
        inquiry_type: 3,
        quoted_rate: +parseFloat(this.quotedRate).toFixed(2),
        unit_id: +(updatedUnit.unit_id_first_available || updatedUnit.sitelink_id),
        unit_dimensions: (updatedUnit.width || this.unitFeatures.width) + " x " +
        (updatedUnit.length || this.unitFeatures.length),
        promotion_id: updatedUnit.concession_id,
        reservation_type: 2
      };

      if (this.info.waiting_id) {
        this.$store.dispatch("updateReservation", payload).then(res => {
          this.getMoveInCost();
        }).catch(err => {
          this.$store.dispatch("handleError", err);
          this.$store.commit("moveInCostLoading", false);
        });
      } else {
        this.getMoveInCost();
      }
    },

    unitUpdated() {
      this.$emit("unitUpdated", this.updatedUnit);
      this.toOptions();
    },

    generateCardTypeName(cardNumber) {
      if (cardNumber.charAt(0) === "5") {
        return "Master Card";
      } else if (cardNumber.charAt(0) === "4") {
        return "Visa";
      } else if (cardNumber.charAt(0) === "6") {
        return "Discover";
      } else if (cardNumber.charAt(0) === "3") {
        return "American Express";
      } else {
        return "";
      }
    },

    toSelect() {
      this.currentStep = 0;
    },

    toOptions() {
      if (this.currentStep === 0 && this.unit && this.unit.unit_id_first_available) {
        const payload = {
          facilityId: this.$route.params.facilityId,
          unit_id: this.unit.unit_id_first_available
        };
        this.$store.dispatch("getSimilarUnits", payload);
      }
      EventBus.$emit("resetPanel");
      this.currentStep = 1;
    },

    toLeaseInfo() {
      const payload = {
        phone: this.info.phone_number,
        email: this.info.email,
        idNumber: this.info.id_number,
        idType: this.info.idType,
        idState: this.info.id_state
      };
      this.$store.dispatch("getIsBlacklisted", payload)
        .then((resp) => {
          this.$store.dispatch(
            "handleError",
            "This customer is currently on our ‘Do Not Rent’ list. Please do not proceed with this customer."
          );
        })
        .catch((err) => {
          EventBus.$emit("resetPanel");
          this.currentStep = 2;
          // 404 is currently the expected response - do not throw exception
          if (err.response.status !== 404) {
            return Promise.reject(err);
          }
        });
    },

    toMoveInInstructions() {
      this.currentStep = 3;
    },

    speederent() {
      this.speederentModal = true;
    },

    doSpeederent() {
      this.speederentModal = false;
      this.$store.dispatch("speederent", this.moveInPayload)
        .then(() => {
          this.$store.dispatch("handleSuccess", "Speederent completed successfully!");
          // this.collapse();
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
        });
    },

    toAddMerchandise() {
      this.currentStep = 4;
    },

    toTakePayment() {
      const lInfo = this.lease;
      if (!lInfo.lien_holders) {
        lInfo.lien_holders = "NO LIENS";
      }
      if (!lInfo.military_contact && !lInfo.military_contact_phone) {
        lInfo.military_contact = "";
        lInfo.military_contact_phone = "";
      }
      if (!lInfo.personal_property_other) {
        lInfo.personal_property_types.filter(propertyType => {
          return propertyType !== "Other";
        });
      }
      this.currentStep = 5;
    },

    toSummary() {
      this.currentStep = 6;
    },

    toConfirmation() {
      this.currentStep = 7;
    },

    collapse() {
      // if (this.promotionOffers && this.unit) {
      //   this.unit.concession_id = this.promotionOffers.this_month.id;
      // }
      this.$emit("collapse");
      this.toOptions();
      this.$store.commit("setGiftCardEligible", false);
      this.$store.commit("clearCreditCard");
      this.$store.commit("setMerchandise", []);
      this.$store.commit("updateMerchandiseCart", []);
      this.$store.commit("updateTenantProtection", "");
    },

    collapseAfterSuccess() {
      this.collapse();
      this.$emit("success");
    },

    submitMoveIn() {
      this.paymentLoading = true;
      this.$store.dispatch("moveIn", this.moveInPayload)
        .then(resp => {
          this.leaseNumber = resp.data.lease_number;
          this.gateCode = resp.data.gate_code;
          this.$store.commit("updateTenantId", resp.data.tenant_id);
          this.$store.commit("updateLedgerId", resp.data.ledger_id);
          this.$store.dispatch("handleSuccess", "Rental processed successfully!");
          this.$store.commit("setRentalData", this.moveInPayload);
          EventBus.$emit("resetPanel");
          this.handleEsignRequest();
          if (this.merchandise.length) {
            this.handleMerchandiseReceipt(resp.data.receipt_number);
          }
          this.handleMoveInNotification();
          this.toConfirmation();
          this.paymentLoading = false;
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
          this.paymentLoading = false;
        });
    },

    handleEsignRequest() {
      const signatureRequestPayload = {
        facilityId: this.$route.params.facilityId,
        first_name: this.info.first_name,
        last_name: this.info.last_name,
        phone: this.info.phone_number,
        mobile_phone: "",
        work_phone: null,
        email: this.info.email,
        mailing_address: this.info.street_address,
        mailing_address_2: null,
        mailing_city: this.info.city,
        mailing_state: this.info.state,
        mailing_postal_code: this.info.postal_code,
        insurance_id: this.insuranceId,
        unit_id: this.unitId,
        lease_number: this.leaseNumber,
        ledger_id: this.info.ledger_id,
        auto_bill_type: this.options.automaticPayments === "1" ? 1 : 0,
        cc_type: this.creditCard.ccType,
        cc_type_name: this.generateCardTypeName(this.creditCard.ccNumber),
        cc_number: this.creditCard.ccNumber,
        cc_expiration_date: this.creditCard.ccExpirationYear + "-" + this.creditCard.ccExpirationMonth,
        billing_name: this.creditCard.firstName + " " + this.creditCard.lastName,
        billing_address: this.info.street_address,
        billing_zip: this.creditCard.billingZip,
        emergency_contact_name: this.lease.emergency_contact_name,
        emergency_contact_phone: this.lease.emergency_contact_phone,
        emergency_contact_email: this.lease.emergency_contact_email,
        is_military: !!(this.lease.military_contact && this.lease.military_contact_phone),
        military_contact: this.lease.military_contact,
        military_contact_phone: this.lease.military_contact_phone,
        personal_property_types: this.lease.personal_property_types,
        personal_property_other: this.lease.personal_property_other,
        lien_holders: this.lease.lien_holders,
        quoted_rate: this.quotedRate,
        tpp_fee: parseFloat(this.options.tenantProtection.premium).toFixed(2),
        tpp_coverage: parseFloat(this.options.tenantProtection.coverage).toFixed(2),
        tenant_id: this.info.external_id,
        with_autobill: this.options.automaticPayments === "1",
        with_tpp: !!(this.options.tenantProtection.coverage),
        vehicle_storage: this.options.tenantProtection === "vehicle",
        gate_code: this.gateCode,
        send_email: true,
        move_in_date: moment(this.options.moveInDate).format("YYYY-MM-DD")
      };
      this.$store.dispatch("createLeaseSignatureRequest", signatureRequestPayload)
        .then(() => {
          this.$store.dispatch("handleSuccess", "Lease emailed successfully!");
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
        });
    },

    // Depricated. This is now handled by the backend
    handleMerchandisePayment(tenant_id) {
      let subtotal = this.merchSubtotal;
      const payload = {
        external_merchandise_items_ids: this.generateExternalMerchandiseItemsIds(this.merchandise),
        payment_amount: parseFloat(subtotal += this.merchTax).toFixed(2),
        payment_method: "credit_card",
        cc_type: this.creditCard.ccType,
        cc_number: this.creditCard.ccNumber,
        cc_cvv: this.creditCard.ccCVV,
        cc_expiration_month: this.creditCard.ccExpirationMonth,
        cc_expiration_year: this.creditCard.ccExpirationYear,
        billing_first_name: this.creditCard.firstName,
        billing_last_name: this.creditCard.lastName,
        billing_zip: this.creditCard.billingZip,
        facilityId: this.$route.params.facilityId
      };
      if (tenant_id) {
        payload.external_tenant_id = tenant_id;
      }
      this.$store.dispatch("merchandisePayment", payload)
        .then(resp => {
          this.$store.dispatch("handleSuccess", "Merchandise sale processed successfully!");
          this.merchReceiptNumber = resp.data.external_receipt_id;
          this.handleMerchandiseReceipt();
        })
        .catch(err => {
          this.merchFailModal = true;
          const validAxiosError = !!(err && err.response && err.response.status);
          if (validAxiosError && err.response.status === 422) {
            let errArr = [];
            for (let error in err.response.data.errors) {
              errArr.push(err.response.data.errors[error][0]);
            }
            this.merchFailMessage = errArr.join(" ");
          } else {
            this.merchFailMessage = err.response.data.message;
          }
        });
    },

    handleMerchandiseReceipt(receipt_number) {
      let subtotal = this.merchSubtotal;
      const payload = {
        facilityId: this.$route.params.facilityId,
        receipt_number: receipt_number,
        payment_amount: parseFloat(subtotal += this.merchTax).toFixed(2),
        payment_method: 0,
        tax_amount: this.merchTax,
        cc_type: this.creditCard.ccType,
        cc_number: this.creditCard.ccNumber,
        email: this.info.email,
        cart: this.merchandise
      };
      this.$store.dispatch("emailMerchandiseReceipt", payload)
        .then(() => {
          this.$store.dispatch("handleSuccess", "Merchandise sale receipt emailed successfully!");
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
        });
    },

    generateExternalMerchandiseItemsIds(merchandise) {
      let externalMerchandiseItemsIds = [];
      merchandise.forEach(item => {
        Array(item.quantity).fill(item.external_id).forEach(id => externalMerchandiseItemsIds.push(id));
      });
      return externalMerchandiseItemsIds;
    },

    handleMoveInNotification() {
      const payload = {
        facilityId: this.$route.params.facilityId,
        tenant_id: this.info.external_id,
        unit_id: this.unitId,
        first_name: this.info.first_name,
        last_name: this.info.last_name,
        mailing_address: this.info.street_address,
        mailing_city: this.info.city,
        mailing_state: this.info.state,
        mailing_postal_code: this.info.postal_code,
        email: this.info.email,
        phone: this.info.phone_number,
        start_date: moment(this.options.moveInDate).format(),
        quoted_rate: this.quotedRate,
        gate_code: this.gateCode,
        concession_id: this.options.selectedPromotion ? parseInt(`${this.options.selectedPromotion}`.replace("-defer", "")) : null,
        insurance_id: this.insuranceId,
        tpp_fee: this.insuranceId ? parseFloat(this.options.tenantProtection.premium).toFixed(2) : 0,
        tpp_coverage: this.insuranceId ? parseFloat(this.options.tenantProtection.coverage).toFixed(2) : 0,
        vehicle_storage: this.options.tenantProtection === "vehicle",
        autopay_enroll: this.options.automaticPayments === "1",
        lease_number: this.leaseNumber,
        payment_method: 0, // since credit card is the only method for call center
        cc_type: this.creditCard.ccType,
        cc_type_name: this.generateCardTypeName(this.creditCard.ccNumber),
        cc_number: this.creditCard.ccNumber,
        charges: this.generateCharges(),
        merchandise_charges: this.generateMerchCharges(),
        rental_total: parseFloat(this.moveInCharges.pms_total_for_movein).toFixed(2),
        merch_total: parseFloat(this.moveInCharges.merchandise).toFixed(2),
        grand_total: parseFloat(this.moveInCharges.total).toFixed(2),
        customer_source: localStorage.getItem("customerSource")
      };
    },

    generateCharges() {
      let charges = [];
      this.moveInCharges.fees.forEach(item => {
        const rentCharge = {
          total_amount: parseFloat(item.cost).toFixed(2),
          description: item.name
        };
        charges.push(rentCharge);
      });
      return charges;
    },

    generateMerchCharges() {
      if (!this.merchandise.length) {
        return [];
      }
      const merchItems = this.merchandise.map(item => {
        return {
          total_amount: (parseFloat(item.price) * item.quantity).toFixed(2),
          description: item.name + " x " + item.quantity + " (" + parseFloat(item.price).toFixed(2) + " each)"
        };
      });
      const merchTaxTotal = [{
        total_amount: parseFloat(this.merchTax).toFixed(2),
        description: "Sales Tax"
      }];
      return merchItems.concat(merchTaxTotal);
    }
  },

  computed: {
    ...mapState({
      creditCard: state => state.tenant.creditCard,
      darkMode: state => state.application.settings.darkTheme,
      defaultPromotionOption: state => state.facilities.defaultPromotionOption,
      info: state => state.tenant.info,
      lease: state => state.tenant.lease,
      merchandise: state => state.facilities.merchandiseCart,
      moveInCharges: state => state.facilities.moveInCost,
      moveInCostEndDate: state => state.facilities.moveInCostEndDate,
      options: state => state.tenant.options,
      promotionOffers: state => state.facilities.promotionOptions,
      quotedRateCustomer: state => state.facilities.quotedRate,
      recording: state => state.call.recording,
      twilioChat: state => state.application.twilioChat,
      unit: state => state.facilities.unit,
      unitFeatures: state => state.facilities.unitFeatures
    }),
    ...mapGetters({
      merchSubtotal: "merchCartSubtotal",
      merchTax: "merchTaxTotal",
      unitId: "unitId"
    }),

    currentStep: {
      get() {
        return this.rentUnitStep;
      },
      set(newVal) {
        this.$emit("currentStep", parseInt(newVal));
      }
    },

    insuranceId() {
      return this.options.tenantProtection && this.options.tenantProtection.external_id ? this.options.tenantProtection.external_id.toString() : null;
    },

    promotionMonth() {
      const promo = this.promotionOffers;
      if (!promo || !this.options.selectedPromotion) {
        return "";
      }
      if (promo.next_month && promo.next_month.id === this.options.selectedPromotion) {
        return "Next Month";
      } else if (promo.next_month && promo.this_month.id === this.options.selectedPromotion) {
        return "This Month";
      } else {
        return "";
      }
    },

    endDate() {
      if (this.moveInCostEndDate === null || this.moveInCostEndDate === undefined) {
        return moment().endOf("month").format("YYYY-MM-DD"); // default to end of month
      }

      return moment(this.moveInCostEndDate).format("YYYY-MM-DD"); // just date... no time
    },

    quotedRate() {
      return this.unit && this.quotedRateCustomer ? this.quotedRateCustomer : "";
    },

    tenantId() {
      if (this.info && this.info.external_id) {
        return this.info.external_id;
      } else {
        return this.unit.tenant_id;
      }
    },

    unitSize() {
      if (this.unitFeatures) {
        return this.unitFeatures.width + " x " + this.unitFeatures.length;
      } else if (this.unit) {
        return this.unit.width + " x " + this.unit.length;
      } else {
        return null;
      }
    },

    productType() {
      if (this.unitFeatures) {
        return this.unitFeatures.product_type;
      } else if (this.unit) {
        return this.unit.product_type;
      } else {
        return null;
      }
    },

    moveInPayload() {
      let moveInCharges = this.moveInCharges || {};
      let adminFee = moveInCharges.fees.filter(fee => fee.name === "Administrative Fee")[0];
      let adminFeeId = adminFee ? String(adminFee.external_id) : null;
      return {
        external_promotion_id: this.options.selectedPromotion ? `${this.options.selectedPromotion}`.replace("-defer", "") : null,
        external_reservation_id: this.info.waiting_id ? String(this.info.waiting_id) : null,
        external_tenant_id: this.info.external_id ? String(this.info.external_id) : null,
        external_tpp_id: this.insuranceId ? String(this.insuranceId) : null,
        external_unit_id: this.unitId ? String(this.unitId) : null,
        external_fee_ids: adminFeeId ? [adminFeeId] : [],
        payment_amount: moveInCharges.pms_total_for_movein,
        cc_type: this.creditCard.ccType,
        start_date: moment(this.options.moveInDate).format("YYYY-MM-DD"),
        end_date: this.endDate,
        cc_number: this.creditCard.ccNumber,
        cc_cvv: this.creditCard.ccCVV,
        cc_exp_month: this.creditCard.ccExpirationMonth,
        cc_exp_year: this.creditCard.ccExpirationYear,
        billing_first_name: this.creditCard.firstName,
        billing_last_name: this.creditCard.lastName,
        billing_address1: this.creditCard.billingAddress,
        billing_state: this.creditCard.billingState,
        billing_zip: this.creditCard.billingZip,
        is_promotion_deferred: this.$store.state.facilities.isPromoDeferred,
        first_name: this.info.first_name,
        last_name: this.info.last_name,
        address1: this.info.street_address,
        city: this.info.city,
        state: this.info.state,
        zip: this.info.postal_code,
        email: this.info.email,
        phone: this.info.phone_number,
        id_number: this.info.id_number,
        id_type: this.info.id_type,
        id_state: this.info.id_state,
        is_business: this.info.is_business,
        business_name: this.info.is_business ? this.info.business_name : "",
        facilityId: this.$route.params.facilityId,
        unit_size: this.unitSize,
        unit_type: this.productType,
        quoted_rate: this.quotedRate,
        promotion_text: this.defaultPromotionOption ? this.defaultPromotionOption.text : "",
        autopay: this.options.automaticPayments === "1",
        customer_source: localStorage.getItem("customerSource"),
        is_gift_card_eligible: this.options.giftCardEligible,
        vehicle_storage: this.options.tenantProtection === "vehicle",
        is_military: this.lease.military_contact.length || this.lease.military_contact_phone.length,
        storage_contents: this.lease.personal_property_types.join(", "),
        merchandise: this.$store.getters.merchandisePayload
      };
    }
  },

  created() {
    EventBus.$on("toSelect", () => {
      this.toSelect();
    });
    EventBus.$on("toOptions", () => {
      this.toOptions();
    });
    EventBus.$on("toLeaseInfo", () => {
      this.toLeaseInfo();
    });
    EventBus.$on("toMoveInInstructions", () => {
      this.toMoveInInstructions();
    });
    EventBus.$on("toAddMerchandise", () => {
      this.toAddMerchandise();
    });
    EventBus.$on("toTakePayment", () => {
      this.toTakePayment();
    });
    if (!this.$store.state.settings.settings.length) {
      this.$store.dispatch("getSettings");
    }
    this.$store.commit("updateTenantProtection", "");
    this.$store.commit("updateMerchandiseCart", []);
  },

  components: {
    StepperNavigation,
    MoveInInstructions,
    ApplicationModal,
    ButtonNavigation,
    AddMerchandise,
    LeaseInfo,
    SideTotal,
    UnitSelect,
    RentalOptions,
    PaymentOptions,
    RentalReview,
    RentalComplete
  }
};
</script>
